import {
  buildArweaveGatewayUrl,
  isKnownArweaveGatewayHost,
  TURBO_ARWEAVE_GATEWAY_HOST,
} from '../shared/arweave/publicArweaveGateway';
import {
  ARWEAVE_PUBLIC_GATEWAY_HOST,
  ARWEAVE_PUBLIC_GATEWAY_HOSTS,
} from '../lib/config';
import { delay } from 'es-toolkit/promise';
import {
  getArweaveGatewayCandidates,
  isArweaveGatewayUrl,
} from './arweaveGatewayFallback';
import { trackCount, trackDuration } from './sentryMetrics';

const DEFAULT_FETCH_TIMEOUT_MS = 10_000;
const ARWEAVE_PRIMARY_RETRIES = 1;
const ARWEAVE_RETRY_DELAY_MS = 50;
const METADATA_NOT_FOUND_STATUSES = new Set([404, 410]);
const METADATA_FETCH_COUNT_METRIC = 'nft_metadata.fetch';
const METADATA_FETCH_DURATION_METRIC = 'nft_metadata.fetch.duration';
const ARWEAVE_TRANSACTION_ID_PATTERN = /^[A-Za-z0-9_-]{43}$/;

type FetchAttempt = {
  url: string;
  status?: number;
  error?: string;
};

type MetadataFetchOutcome = 'success' | 'failure';

class MetadataFetchError extends Error {
  attempts: FetchAttempt[];

  constructor(message: string, attempts: FetchAttempt[]) {
    super(message);
    this.name = 'MetadataFetchError';
    this.attempts = attempts;
  }
}

export function isMetadataUnavailableError(error: unknown): boolean {
  if (!(error instanceof MetadataFetchError)) {
    return false;
  }

  const statuses = error.attempts
    .filter((attempt) => attempt.status !== undefined)
    .map((attempt) => attempt.status as number);

  if (statuses.length === 0) {
    return false;
  }

  const primaryStatus = statuses[0];
  if (METADATA_NOT_FOUND_STATUSES.has(primaryStatus)) {
    return true;
  }

  return statuses.every((status) => METADATA_NOT_FOUND_STATUSES.has(status));
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function normalizeMetadataUri(uri: string): string {
  if (uri.startsWith('ipfs://')) {
    const hash = uri.replace('ipfs://', '');
    return `https://ipfs.io/ipfs/${hash}`;
  }

  if (uri.startsWith('ar://')) {
    const match = /^ar:\/\/([^/?#]+)(.*)$/.exec(uri);
    if (!match) {
      return uri;
    }
    const [, target, suffix] = match;

    if (ARWEAVE_TRANSACTION_ID_PATTERN.test(target)) {
      return buildArweaveGatewayUrl(
        `${target}${suffix}`,
        ARWEAVE_PUBLIC_GATEWAY_HOST,
      );
    }

    return `https://${target}.${ARWEAVE_PUBLIC_GATEWAY_HOST}${suffix || '/'}`;
  }

  if (!uri.startsWith('http://') && !uri.startsWith('https://')) {
    return buildArweaveGatewayUrl(uri, ARWEAVE_PUBLIC_GATEWAY_HOST);
  }

  return uri;
}

function toSafeUrl(rawUrl: string): string {
  try {
    const parsedUrl = new URL(rawUrl);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return rawUrl;
  }
}

function toGatewayLabel(rawUrl: string): string {
  try {
    const host = new URL(rawUrl).hostname.toLowerCase();
    if (
      host === TURBO_ARWEAVE_GATEWAY_HOST ||
      host.endsWith(`.${TURBO_ARWEAVE_GATEWAY_HOST}`)
    ) {
      return TURBO_ARWEAVE_GATEWAY_HOST;
    }

    if (isKnownArweaveGatewayHost(host, ARWEAVE_PUBLIC_GATEWAY_HOSTS)) {
      return host;
    }

    if (host === 'ipfs.io' || host.endsWith('.ipfs.io')) {
      return 'ipfs.io';
    }

    return 'other';
  } catch {
    return 'other';
  }
}

function getPrimaryStatus(attempts: FetchAttempt[]): number | undefined {
  return attempts.find((attempt) => attempt.status !== undefined)?.status;
}

function classifyFailureReason(attempts: FetchAttempt[]): string {
  const primaryStatus = getPrimaryStatus(attempts);
  if (primaryStatus !== undefined) {
    if (METADATA_NOT_FOUND_STATUSES.has(primaryStatus)) {
      return 'not_found';
    }

    if (primaryStatus === 429) {
      return 'rate_limited';
    }

    if (primaryStatus === 408) {
      return 'timeout';
    }

    if (primaryStatus >= 500) {
      return 'server_error';
    }

    if (primaryStatus >= 400) {
      return 'client_error';
    }
  }

  const messages = attempts
    .map((attempt) => attempt.error?.toLowerCase())
    .filter((message): message is string => typeof message === 'string');

  if (
    messages.some(
      (message) => message.includes('aborted') || message.includes('timeout'),
    )
  ) {
    return 'timeout';
  }

  if (
    messages.some(
      (message) =>
        message.includes('not valid json') ||
        message.includes('unexpected token'),
    )
  ) {
    return 'invalid_json';
  }

  return 'network_or_unknown';
}

function trackMetadataFetchMetric(
  outcome: MetadataFetchOutcome,
  options: {
    initialUrl: string;
    finalUrl: string;
    attemptCount: number;
    durationMs: number;
    statusCode?: number;
    failureReason?: string;
  },
): void {
  const initialGateway = toGatewayLabel(options.initialUrl);
  const finalGateway = toGatewayLabel(options.finalUrl);

  const attributes: Record<string, string | number | boolean> = {
    component: 'metadata_fetch',
    outcome,
    gateway: finalGateway,
    fallback_used: initialGateway !== finalGateway,
    attempt_count: options.attemptCount,
  };

  if (options.statusCode !== undefined) {
    attributes.status_code = options.statusCode;
  }

  if (options.failureReason) {
    attributes.failure_reason = options.failureReason;
  }

  trackCount(METADATA_FETCH_COUNT_METRIC, 1, attributes);
  trackDuration(
    METADATA_FETCH_DURATION_METRIC,
    Math.max(options.durationMs, 0),
    attributes,
  );
}

function shouldRetryArweaveStatus(status: number): boolean {
  return status === 404 || status === 408 || status === 429 || status >= 500;
}

function formatAttempts(attempts: FetchAttempt[]): string {
  if (attempts.length === 0) {
    return 'No attempts made';
  }

  return attempts
    .map((attempt) => {
      if (attempt.status !== undefined) {
        return `${attempt.url} -> ${attempt.status}`;
      }

      return `${attempt.url} -> ${attempt.error ?? 'network error'}`;
    })
    .join(' | ');
}

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    DEFAULT_FETCH_TIMEOUT_MS,
  );

  try {
    return await fetch(url, {
      signal: controller.signal,
      headers: {
        Accept: 'application/json,text/plain,*/*',
      },
    });
  } finally {
    clearTimeout(timeout);
  }
}

function parseMetadataJson(
  responseText: string,
  responseContentType: string | null,
  sourceUrl: string,
): unknown {
  try {
    return JSON.parse(responseText);
  } catch {
    const contentType = responseContentType || 'unknown';
    const preview = responseText.slice(0, 80).replace(/\s+/g, ' ');
    throw new Error(
      `Metadata response from ${toSafeUrl(sourceUrl)} is not valid JSON (content-type: ${contentType}, preview: "${preview}")`,
    );
  }
}

export async function fetchMetadataJson(uri: string): Promise<unknown> {
  const startedAt = Date.now();
  const normalizedUrl = normalizeMetadataUri(uri);
  const candidates = getArweaveGatewayCandidates(normalizedUrl);
  const attempts: FetchAttempt[] = [];

  for (
    let candidateIndex = 0;
    candidateIndex < candidates.length;
    candidateIndex += 1
  ) {
    const candidateUrl = candidates[candidateIndex];
    const isPrimaryArweaveCandidate =
      candidateIndex === 0 && isArweaveGatewayUrl(candidateUrl);
    const maxRetries = isPrimaryArweaveCandidate ? ARWEAVE_PRIMARY_RETRIES : 0;

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        const response = await fetchWithTimeout(candidateUrl);
        const responseUrl = response.url || candidateUrl;
        attempts.push({
          url: toSafeUrl(responseUrl),
          status: response.status,
        });

        if (!response.ok) {
          if (
            attempt < maxRetries &&
            shouldRetryArweaveStatus(response.status)
          ) {
            await delay(ARWEAVE_RETRY_DELAY_MS * (attempt + 1));
            continue;
          }

          break;
        }

        const responseText = await response.text();
        if (!responseText) {
          throw new Error(
            `Metadata response from ${toSafeUrl(candidateUrl)} was empty`,
          );
        }

        const metadata = parseMetadataJson(
          responseText,
          response.headers.get('content-type'),
          candidateUrl,
        );

        trackMetadataFetchMetric('success', {
          initialUrl: normalizedUrl,
          finalUrl: responseUrl,
          attemptCount: attempts.length,
          durationMs: Date.now() - startedAt,
          statusCode: response.status,
        });

        return metadata;
      } catch (error) {
        const normalizedError = toError(error);
        attempts.push({
          url: toSafeUrl(candidateUrl),
          error: normalizedError.message,
        });

        if (attempt < maxRetries) {
          await delay(ARWEAVE_RETRY_DELAY_MS * (attempt + 1));
          continue;
        }

        break;
      }
    }
  }

  const finalAttemptUrl =
    attempts.length > 0 ? attempts[attempts.length - 1].url : normalizedUrl;
  const primaryStatus = getPrimaryStatus(attempts);
  trackMetadataFetchMetric('failure', {
    initialUrl: normalizedUrl,
    finalUrl: finalAttemptUrl,
    attemptCount: attempts.length,
    durationMs: Date.now() - startedAt,
    statusCode: primaryStatus,
    failureReason: classifyFailureReason(attempts),
  });

  throw new MetadataFetchError(
    `Failed to fetch metadata JSON. Attempts: ${formatAttempts(attempts)}`,
    attempts,
  );
}
